import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import type { InvoiceListItem } from '@repo/shared-types';
import { Button } from '@repo/ui/src/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@repo/ui/src/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@repo/ui/src/components/ui/dialog';
import { Label } from '@repo/ui/src/components/ui/label';
import { cn } from '@repo/ui/src/lib/utils';
import { Check } from 'lucide-react';
import { formatVnd } from '../../../lib/formatVnd';
import { MANUAL_SOURCE_OPTIONS, type ManualSource } from '../../../lib/paymentSource';
import {
  partitionForBulkDelete,
  partitionForMarkPaid,
  sumSelectedTotals,
} from '../../../lib/bulkInvoiceSelection';
import { useBulkDeleteInvoices, useBulkDueDateInvoices, useBulkMarkPaidInvoices } from '../../../hooks/useInvoices';
import { useServerNow } from '../../../hooks/useServerNow';
import { useCan } from '../../../hooks/useCan';
import { DatePicker } from '../../../components/pickers/DatePicker';
import { SelectionBar } from '../../../components/SelectionBar';

interface BulkActionsBarProps {
  /** The currently listed (filtered/sorted) invoices — selection math runs
   *  over whichever of these are ticked. */
  invoices: InvoiceListItem[];
  selectedIds: Set<string>;
  onClearSelection: () => void;
}

type DialogKind = 'none' | 'markPaid' | 'delete' | 'dueDate';

// Bottom selection bar — same interaction family as "chế độ xếp lớp"'s bar on
// the Thời khoá biểu screen (TimetablePage.tsx's MoveModeBar): both adopt the
// shared `SelectionBar` (components/SelectionBar.tsx), which owns the bar's
// anatomy (fixed positioning, z-index, bottom-edge claim). This file owns
// eligibility, dialogs and mutations only.
export function BulkActionsBar({ invoices, selectedIds, onClearSelection }: BulkActionsBarProps) {
  const { t } = useTranslation('invoices');
  // Same source labels RecordPaymentDrawer shows (design doc §2: "same
  // options as RecordPaymentDrawer's") — read from the `payments` namespace
  // it owns, rather than duplicating the strings here.
  const { t: tp } = useTranslation('payments');
  const [dialogKind, setDialogKind] = useState<DialogKind>('none');
  const [source, setSource] = useState<ManualSource>('MANUAL_BANK');
  const [dueDateValue, setDueDateValue] = useState('');
  const [dueDateError, setDueDateError] = useState<string | null>(null);

  const bulkMarkPaid = useBulkMarkPaidInvoices();
  const bulkDelete = useBulkDeleteInvoices();
  const navigate = useNavigate();
  // THI-259: a bulk delete is the moment someone may want the paper trail.
  const canSeeOrgLog = useCan('manage:settings');
  const bulkDueDate = useBulkDueDateInvoices();
  // VN wall-clock "today" as YYYY-MM-DD — same UTC-getter convention
  // sessionTime.ts's currentMonth/prevMonth read `useServerNow()` with, so
  // this stays correct through a VN-morning/UTC-day mismatch. Plain string
  // comparison against it is safe: both sides are always YYYY-MM-DD.
  const now = useServerNow();
  const todayKey = `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}-${String(now.getUTCDate()).padStart(2, '0')}`;

  const selected = invoices.filter((inv) => selectedIds.has(inv.id));
  if (selected.length === 0) return null;

  const total = sumSelectedTotals(selected);
  const markPaid = partitionForMarkPaid(selected);
  // Bulk re-date is DRAFT-only, same eligibility as bulk delete — reuse its
  // partition rather than re-deriving it (design spec §4-FE: "D = DRAFT
  // count, same partition source as bulk delete").
  const del = partitionForBulkDelete(selected);
  const isBusy = bulkMarkPaid.isPending || bulkDelete.isPending || bulkDueDate.isPending;

  function openMarkPaidDialog() {
    setSource('MANUAL_BANK'); // reset each time — a leftover pick from an earlier, cancelled dialog must not carry over
    setDialogKind('markPaid');
  }

  function confirmMarkPaid() {
    bulkMarkPaid.mutate(
      { invoiceIds: selected.map((inv) => inv.id), source },
      {
        onSuccess: (result) => {
          toast.success(
            result.skipped.length > 0
              ? t('bulk.toast.markPaidSuccessWithSkipped', { count: result.marked, skipped: result.skipped.length })
              : t('bulk.toast.markPaidSuccess', { count: result.marked }),
          );
          onClearSelection();
          setDialogKind('none');
        },
      },
    );
  }

  function confirmDelete() {
    bulkDelete.mutate(
      { invoiceIds: selected.map((inv) => inv.id) },
      {
        onSuccess: (result) => {
          toast.success(
            result.skipped.length > 0
              ? t('bulk.toast.deleteSuccessWithSkipped', { count: result.deleted, skipped: result.skipped.length })
              : t('bulk.toast.deleteSuccess', { count: result.deleted }),
            canSeeOrgLog && result.deleted > 0
              ? { action: { label: t('bulk.toast.viewLog'), onClick: () => navigate('/settings/audit?group=money') } }
              : undefined,
          );
          onClearSelection();
          setDialogKind('none');
        },
      },
    );
  }

  function openDueDateDialog() {
    setDueDateValue(''); // reset each time — no leftover pick from an earlier, cancelled dialog
    setDueDateError(null);
    setDialogKind('dueDate');
  }

  function confirmDueDate() {
    // Same FE-side today-or-future check the confirm dialog on CloseMonthPage
    // uses (design spec §2/§4-FE) — the BE re-validates regardless
    // (parseFutureDueDate), this only saves a round trip on an obvious miss.
    if (dueDateValue < todayKey) {
      setDueDateError(t('bulk.dueDateDialog.past'));
      return;
    }
    bulkDueDate.mutate(
      { invoiceIds: selected.map((inv) => inv.id), dueDate: dueDateValue },
      {
        onSuccess: (result) => {
          toast.success(
            result.skipped.length > 0
              ? t('bulk.toast.dueDateSuccessWithSkipped', { count: result.updated, skipped: result.skipped.length })
              : t('bulk.toast.dueDateSuccess', { count: result.updated }),
          );
          onClearSelection();
          setDialogKind('none');
        },
      },
    );
  }

  return (
    <>
      <SelectionBar
        id="bulk-actions"
        ariaLabel={t('bulk.bar.ariaLabel')}
        active
        summary={t('bulk.bar.summary', { count: selected.length, total: formatVnd(total) })}
        actions={
          <>
            <Button
              type="button"
              size="sm"
              disabled={markPaid.eligibleCount === 0 || isBusy}
              onClick={openMarkPaidDialog}
            >
              {t('bulk.bar.markPaid', { eligible: markPaid.eligibleCount, count: selected.length })}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={del.eligibleCount === 0 || isBusy}
              onClick={openDueDateDialog}
            >
              {t('bulk.bar.dueDate', { draft: del.eligibleCount, count: selected.length })}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="border-red-200 text-red-700 hover:bg-red-50 hover:text-red-800"
              disabled={del.eligibleCount === 0 || isBusy}
              onClick={() => setDialogKind('delete')}
            >
              {t('bulk.bar.delete', { draft: del.eligibleCount, count: selected.length })}
            </Button>
            <Button type="button" variant="ghost" size="sm" onClick={onClearSelection} disabled={isBusy}>
              {t('bulk.bar.clear')}
            </Button>
          </>
        }
      />

      <Dialog open={dialogKind === 'markPaid'} onOpenChange={(o) => !o && setDialogKind('none')}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('bulk.markPaidDialog.title')}</DialogTitle>
            <DialogDescription>
              {markPaid.skippedCount > 0
                ? t('bulk.markPaidDialog.descriptionWithSkipped', {
                    eligible: markPaid.eligibleCount,
                    skipped: markPaid.skippedCount,
                  })
                : t('bulk.markPaidDialog.description', { eligible: markPaid.eligibleCount })}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            <Label>{t('bulk.markPaidDialog.source')}</Label>
            <div className="flex flex-wrap gap-2">
              {MANUAL_SOURCE_OPTIONS.map((opt) => {
                const isSelected = source === opt.value;
                const Icon = opt.icon;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setSource(opt.value)}
                    className={cn(
                      'inline-flex items-center gap-2 rounded-md border px-3 py-2 text-sm font-medium transition-all',
                      isSelected
                        ? 'border-brand-500 bg-brand-50 text-brand-700 ring-1 ring-brand-500'
                        : 'border-surface-200 text-surface-600 hover:border-brand-300 hover:bg-brand-50/50',
                    )}
                  >
                    <Icon className={cn('h-4 w-4', isSelected ? 'text-brand-600' : 'text-surface-400')} strokeWidth={1.8} />
                    {tp(`drawer.source.${opt.value}`)}
                    {isSelected && <Check className="h-3.5 w-3.5 text-brand-600" strokeWidth={2.5} />}
                  </button>
                );
              })}
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setDialogKind('none')} disabled={bulkMarkPaid.isPending}>
              {t('bulk.markPaidDialog.cancel')}
            </Button>
            <Button type="button" onClick={confirmMarkPaid} disabled={bulkMarkPaid.isPending}>
              {bulkMarkPaid.isPending ? t('bulk.markPaidDialog.confirming') : t('bulk.markPaidDialog.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={dialogKind === 'delete'} onOpenChange={(o) => !o && setDialogKind('none')}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('bulk.deleteDialog.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {del.skippedCount > 0
                ? t('bulk.deleteDialog.descriptionWithSkipped', { eligible: del.eligibleCount, skipped: del.skippedCount })
                : t('bulk.deleteDialog.description', { eligible: del.eligibleCount })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bulkDelete.isPending}>{t('bulk.deleteDialog.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={bulkDelete.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {bulkDelete.isPending
                ? t('bulk.deleteDialog.confirming')
                : t('bulk.deleteDialog.confirm', { eligible: del.eligibleCount })}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={dialogKind === 'dueDate'} onOpenChange={(o) => !o && setDialogKind('none')}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('bulk.dueDateDialog.title')}</DialogTitle>
            <DialogDescription>
              {del.skippedCount > 0
                ? t('bulk.dueDateDialog.descriptionWithSkipped', { eligible: del.eligibleCount, skipped: del.skippedCount })
                : t('bulk.dueDateDialog.description', { eligible: del.eligibleCount })}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            <Label htmlFor="bulk-due-date-input">{t('bulk.dueDateDialog.label')}</Label>
            <DatePicker
              id="bulk-due-date-input"
              value={dueDateValue}
              min={todayKey}
              onChange={(next) => {
                setDueDateValue(next);
                setDueDateError(null);
              }}
              className="w-full"
            />
            {dueDateError && <p className="text-xs text-destructive">{dueDateError}</p>}
          </div>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setDialogKind('none')} disabled={bulkDueDate.isPending}>
              {t('bulk.dueDateDialog.cancel')}
            </Button>
            <Button type="button" onClick={confirmDueDate} disabled={bulkDueDate.isPending || !dueDateValue}>
              {bulkDueDate.isPending
                ? t('bulk.dueDateDialog.confirming')
                : t('bulk.dueDateDialog.confirm', { eligible: del.eligibleCount })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
